import { type ReactNode, useEffect, useState } from "react";
// @ts-ignore
import { reset } from "sync-scroll";
import "sync-scroll";
import { useDispatch, useSelector } from "react-redux";

import { IconType } from "../common/components/icon/IconType";
import { Icon } from "../common/components/icon/Icon";
import ProductComparisonCard from "./ProductComparisonCard";
import ProductComparisonSmallCard from "./ProductComparisonSmallCard";
import { useOnMount } from "../common/hooks/useOnMount";
import { type PdpPageProductData } from "../ProductDetails/models/PdpPageProductData";
import EmptyComparisonPage from "./EmptyComparison";
import ComercialTable from "./ComercialTable";
import AvailabilityTable from "./AvailabilityTable";
import ProductInformationTable from "./ProductInformationTable";
import { userIsDistributor } from "../utils/roles";
import { selectProductComparisonList } from "./redux/productComparisonSelectors";
import { addBaseProductDataAction, requestAvailabilityData, requestPriceData } from "../redux/products/actions";
import { type RootState } from "../redux/store";
import { addToComparisonBulk, clearComparisonList } from "./redux/actions";
import {
  trackAddToComparisonClick,
  trackClearComparisonClick,
  trackPCDownload,
  trackProductComparisonPageNavigation,
} from "./analytics";
import { selectProductDescriptor } from "../redux/products/productsSelectors";
import { Permission, userHasPermission } from "../utils/permissions";
import { ReduxImperialToggle } from "../feature/UnitMode/UnitModeToggle";
import { IncludesSurchargeDisclaimer } from "../common/components/Messages/IncludesSurchargeDisclaimer";
import { type PdpPageProductDataDto } from "../ProductDetails/dto/PdpPageProductDataDto";
import { transformPdpProduct } from "../ProductDetails/transformers/PdpPageProductDataTransformer";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { IconSize } from "~/common/components/icon/IconSize";
import { useUnitMode } from "~/feature/UnitMode/useUnitMode";
import { downloadPC } from "~/ProductComparison/api/DownloadFile";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { DownloadClickLink } from "~/common/components/Link";
import { QuickEntryPopup } from "~/QuickEntryPopup/v2/QuickEntryPopup";
import { Trans, useTranslation } from "react-i18next";
import { hasProductConfiguredPC } from "~/QuickEntryPopup/helpers";
import { addMessage } from "~/redux/globalMessages/actions";
import {
  type AllScreenSizes,
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { getStore } from "~/redux/getStore";
import { Alert } from "~/common/m3/Alert/Alert";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface Props {
  data: PdpPageProductData[];
}

const ProductComparisonPage = ({ data }: Props) => {
  const { t } = useTranslation();
  const { maxSize } = window.SCHA.productComparisonList;

  const size = useNumericScreenSize();
  const isDesktop = size >= SIZE_DESKTOP_S;

  const [renderChildren, setRenderChildren] = useState(false);
  const [scrollAtCards, setScrollAtCards] = useState(false);
  const { val: statusModal, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);
  const productCodes = useSelector(selectProductComparisonList);
  const unitMode = useUnitMode();

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const pageContainsSurcharge =
    productDescriptorList.length > 0 &&
    productDescriptorList.some(
      (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
    );

  const pageContainsTariffSurcharge =
    productDescriptorList.length > 0 &&
    productDescriptorList.some(
      (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
    );

  useEffect(() => {
    const yVal = 210;
    const listenScrollEvent = () => {
      setScrollAtCards(window.scrollY > yVal);
    };
    window.addEventListener("scroll", listenScrollEvent);
    return () => window.removeEventListener("scroll", listenScrollEvent);
  }, [isDesktop]);

  const productDesignations = productDescriptorList.map((descriptor) => descriptor.base.designation);

  const dispatch = useDispatch();
  const handleRemoveAllClick = () => {
    dispatch(clearComparisonList());
    trackClearComparisonClick(productDesignations);
  };

  useOnMount(() => trackProductComparisonPageNavigation(productDesignations));

  useOnMount(() => {
    data.forEach((product) => {
      if (userHasPermission(Permission.PRICE)) {
        dispatch(requestPriceData(product.code, getInitialPriceReference()));
      }
      if (userHasPermission(Permission.AVAILABILITY)) {
        dispatch(requestAvailabilityData(product.code));
      }
    });
    setRenderChildren(true);
  });

  const placeholders = Array.from({ length: maxSize - productCodes.length }, (_, idx) => productCodes.length + idx);

  const tables = document.querySelectorAll("table");
  tables.forEach((table, idx) => {
    if (idx % 2 === 0 || idx % 2 === 1) {
      table?.parentElement?.classList.add("remove-scroll");
      table?.parentElement?.parentElement?.classList.add("syncscroll");
      table?.parentElement?.parentElement?.setAttribute("name", "myElements");
    }
    reset();
  });

  const productDescriptors = useSelector((state: RootState) => {
    return productCodes.map((productCode) => selectProductDescriptor(state, { productCode, priceReference: "" }));
  });

  const downloadFunc = (type: "PDF" | "XLSX") => {
    trackPCDownload(type, productDesignations);
    downloadPC(type, unitMode);
  };

  const colSize = COL_SIZES[size];

  useEffect(() => {
    reset();
  }, [size, ...productDescriptors]);

  const quickEntryPopup = (
    <QuickEntryPopup
      onClose={closeModal}
      maxAmount={maxSize - productCodes.length}
      validateRow={(row, itemsPopup = []) => {
        const errors: ReactNode[] = [];
        const filteredItemsPopup = itemsPopup
          .filter((item) => item.id !== row.id)
          .map(function (item) {
            return item.productId;
          });
        const dataPagePlusPopup = [...productDesignations, ...filteredItemsPopup];
        if (dataPagePlusPopup.includes(row.product.designation)) {
          errors.push(
            <Alert className="product-id" severity={"error"}>
              <Trans i18nKey={"bulkUpload.productComparison.error.repeatedProduct"}>
                The product <span>{{ designation: row.product.designation }}</span> is already in comparison list.
                Choose different product to compare.
              </Trans>
            </Alert>,
          );
        }
        return errors;
      }}
      onAdd={async (form, setIsSending, onClose) => {
        const items = form.items.filter(hasProductConfiguredPC);
        const arrProd = items.map((item) => item.product.code);
        setIsSending(true);
        await dispatch(addToComparisonBulk(arrProd));
        const designations = items.map((item) => item.product.designation);
        trackAddToComparisonClick(designations, true, "Product Comparison");
        setIsSending(false);
        if (arrProd.length > 0) {
          dispatch(addMessage("info", t("addToPC.success", { count: arrProd.length })));
        }
        onClose();
      }}
      addButtonLabel={t("comparisonPage.modal.add.submitBtn")}
    />
  );

  return (
    <div className={"scha__comparison-page__root"}>
      {productCodes.length > 0 ? (
        <>
          <PageTitle
            className="scha__comparison-page__header"
            title={t("comparisonPage.title", { length: productCodes.length, maxSize })}
          />
          {/* @ts-ignore */}
          <div className="syncscroll scha__comparison-page__cards-container" name="myElements">
            <div className={scrollAtCards ? "cards-area-scrolled" : "cards-area"}>
              <div className={"scha__comparison-page__clear-list-area"}>
                <div className={"scha__comparison-page__imperial"}>
                  <ReduxImperialToggle className={"scha__comparison-page__imperial-toggle"} page="Comparison List" />
                </div>
                <div className={"scha__comparison-page__options"}>
                  <div className={"d-flex flex-direction-column"}>
                    <div className="scha__comparison-page__download-title">
                      {t("comparisonPage.downloadFile.title")}
                    </div>
                    <div className="scha__comparison-page__download-container">
                      <DownloadClickLink onClick={() => downloadFunc("PDF")}>
                        {t("downloadFile.type.pdf")}
                      </DownloadClickLink>
                      <DownloadClickLink onClick={() => downloadFunc("XLSX")}>
                        {t("downloadFile.type.excel")}
                      </DownloadClickLink>
                    </div>
                  </div>

                  <div className="scha__comparison-page__clear-list" onClick={handleRemoveAllClick}>
                    <Icon type={IconType.Bin} className="scha__comparison-page__trash-icon" />
                    {t("comparisonPage.clear.list")}
                  </div>
                </div>
              </div>
              <div className="d-flex cards-list">
                {renderChildren &&
                  productCodes.map((code) =>
                    scrollAtCards ? (
                      <ProductComparisonSmallCard key={code} productCode={code} />
                    ) : (
                      <ProductComparisonCard key={code} productCode={code} />
                    ),
                  )}
                {placeholders.map((items, index) => (
                  <>
                    {index === 0 ? (
                      <div className="scha__comparison-page__add-card" key={index} onClick={openModal}>
                        <div className={"scha__comparison-page__add-card__icon"}>
                          <Icon type={IconType.Plus} size={isDesktop ? IconSize.FONT_24 : IconSize.FONT_20} />
                        </div>
                        <h1 className={"scha__comparison-page__add-card__title"}>
                          {t("comparisonPage.addProduct.title")}
                        </h1>
                      </div>
                    ) : (
                      <div className="scha__comparison-page__empty-card" key={index} />
                    )}
                  </>
                ))}
              </div>
            </div>
          </div>
          <div className="scha__comparison-page__table-container">
            {/* @ts-ignore */}
            {userIsDistributor() && (
              <>
                <ComercialTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
                <AvailabilityTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
              </>
            )}
            <ProductInformationTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
          </div>
          <div className="scha__comparison-page__disclaimers">
            <Alert severity={"info"}>{t("comparisonPage.selectUnitType")}</Alert>
            {
              <IncludesSurchargeDisclaimer
                hasSurcharge={pageContainsSurcharge}
                tariffSurcharge={pageContainsTariffSurcharge}
              />
            }
          </div>
        </>
      ) : (
        <EmptyComparisonPage openQuickEntry={openModal} />
      )}
      {statusModal && quickEntryPopup}
    </div>
  );
};

export function createProductComparisonPage({ json }: { json: string }) {
  let products: PdpPageProductData[];
  if (json) {
    products = (JSON.parse(json) as PdpPageProductDataDto[]).map((item) => transformPdpProduct(item, null));
    getStore().dispatch(addBaseProductDataAction(products));
  } else {
    products = [];
  }
  return <ProductComparisonPage data={products} />;
}

type SizeMap = Record<AllScreenSizes, number>;
const COL_SIZES: SizeMap = {
  [SIZE_BELOW_PHONE]: 248,
  [SIZE_PHONE]: 248,
  [SIZE_TABLET]: 248,
  [SIZE_DESKTOP_S]: 330,
  [SIZE_DESKTOP_M]: 378,
  [SIZE_DESKTOP_L]: 304,
};
